import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, X, FileText, ExternalLink, ArrowRight, Info, Shield, Sun, Eye, Award, Cloud, Home as HomeIcon } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";
import logo3M from "@/assets/3m-logo.png.asset.json";
import solarScreenLogo from "@/assets/solar-screen.webp.asset.json";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";
import { CallbackRail } from "@/components/CallbackRail";

export const Route = createFileRoute("/3m-prestige")({
  head: () => ({
    meta: [
      { title: "3M Prestige solfilm — Prestige 70 & Exterior | Auktoriserad återförsäljare BBN Nordic" },
      {
        name: "description",
        content:
          "3M Prestige (Prestige 70 invändigt + Prestige Exterior utvändigt) — nanokeramisk solfilm utan metall. Specifikationer från 3M:s officiella datablad, prisspann och rådgivning. Auktoriserad 3M-återförsäljare och installatör i Sverige sedan 2008.",
      },
      { property: "og:title", content: "3M Prestige solfilm — Prestige 70 & Exterior" },
      {
        property: "og:description",
        content:
          "Officiella specifikationer för 3M Prestige 70 och Prestige Exterior. Hög klarhet, ingen toning, 99 % UV-skydd. Auktoriserad 3M-installatör i hela Sverige.",
      },
      { property: "og:url", content: "https://bbnnordic.se/3m-prestige" },
      { property: "og:image", content: BBN_PHOTOS.prestigeHero },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/3m-prestige" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "3M Prestige solfilm",
          category: "Fönsterfilm / Premium-solfilm",
          description:
            "3M Prestige-serien: solfilm med nanoteknik istället för metall. Klar sikt, naturligt ljus, kraftig värme- och UV-reduktion utan toning.",
          image: BBN_PHOTOS.prestigeHero,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          material: "Nanokeramisk polyester, flerlagerlaminat (>200 lager)",
          url: "https://bbnnordic.se/3m-prestige",
          additionalProperty: [
            { "@type": "PropertyValue", name: "UV-reduktion", value: "99 %", unitText: "%" },
            { "@type": "PropertyValue", name: "Infraröd blockering (IR)", value: "97 %", unitText: "%" },
            { "@type": "PropertyValue", name: "Garanti", value: "10 år produktgaranti + 10 år arbetsgaranti" },
            { "@type": "PropertyValue", name: "Auktoriserad installatör", value: "3M Authorized Dealer sedan 2008" },
          ],
          hasVariant: [
            { "@type": "Product", name: "3M Prestige 70", description: "68 % VLT — högsta ljusgenomsläpp, klart utseende" },
            { "@type": "Product", name: "3M Prestige 50 Exterior", description: "Utvändig montering för isolerglas" },
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "1100",
            priceSpecification: { "@type": "PriceSpecification", price: "1100", priceCurrency: "SEK" },
            price: "1100",
            highPrice: "1600",
            availability: "https://schema.org/InStock",
            priceValidUntil: "2026-12-31",
            seller: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Andrée Johnsson" },
              reviewBody: "Kan varmt rekommenderas. Vi hade en undermålig solfilm som konkurrent satt upp, Marcus knackade på när han var hos grannen och sa att det där kan göras bättre.",
            },
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Christian Backe" },
              reviewBody: "Vi hade problem med att våra fönster släppte in väldigt mycket värme och valde en film som BBN satte med bästa resultat. Kan varmt rekommendera!",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "3M Prestige", item: "https://bbnnordic.se/3m-prestige" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Vad är 3M Prestige och hur skiljer den sig från vanlig solfilm?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "3M Prestige är en nanokeramisk solfilm uppbyggd av över 200 mikroskopiska polymerlager istället för en metallbeläggning. Det ger hög värme- och UV-reduktion utan spegelblank fasad, utan toning och utan att störa mobil-, wifi- eller GPS-signaler.",
              },
            },
            {
              "@type": "Question",
              name: "Vilken Prestige-variant ska jag välja — invändig eller utvändig?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "För invändig montering använder vi 3M Prestige 70 (68 % VLT), som behåller utsikten. För utvändig montering finns Prestige 40 Exterior, Prestige 70 Exterior och Prestige 90 Exterior. Vi väljer utifrån glastyp, monteringssida och hur mycket sol fönstret får.",
              },
            },
            {
              "@type": "Question",
              name: "Hur mycket värme och UV blockerar 3M Prestige?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "Enligt 3M:s officiella datablad reflekterar Prestige upp till ca 97 % av infraröd värmestrålning och blockerar 99 % av UV-strålningen. Prestige 70 har 50 % TSER (total solenergireduktion) på 1/4\" Clear glas.",
              },
            },
            {
              "@type": "Question",
              name: "Vilken garanti gäller på 3M Prestige?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "Som auktoriserad 3M-installatör utfärdar BBN Nordic upp till 15 års produktgaranti från 3M på Prestige-serien vid invändig montering. Garantin täcker färgförändring, sprickbildning och delaminering — och i många fall även glasskada som filmen orsakat.",
              },
            },
            {
              "@type": "Question",
              name: "Vad kostar 3M Prestige solfilm?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "Indikativt från ca 850 kr/m² inkl. moms och montage för Prestige-serien. Exakt pris efter mätning eftersom det beror på fönsterstorlek, glastyp, höjd och åtkomst. Offert är alltid kostnadsfri.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: PrestigePage,
});

/**
 * VIKTIGT: Tekniska siffror i jämförelsetabellen nedan är indikativa intervall
 * baserade på 3M:s officiella datablad för Prestige 70.
 * Verifiera EXAKTA värden mot aktuellt 3M-datablad innan publicering/offert.
 */

type Row = {
  label: string;
  icon: typeof Sun;
  prestige: string;
  tonad: string;
  ingen: string;
};

const comparison: Row[] = [
  {
    label: "Ljusgenomsläpp (synligt ljus)",
    icon: Eye,
    prestige: "68 %",
    tonad: "5–35 %",
    ingen: "~88 %",
  },
  {
    label: "Solvärmereduktion (TSER)",
    icon: Sun,
    prestige: "Hög (50 %)",
    tonad: "Medel (≈ 30–50 %)",
    ingen: "Ingen",
  },
  {
    label: "IR-reduktion (värmestrålning)",
    icon: Sun,
    prestige: "Upp till ≈ 97 %",
    tonad: "≈ 40–80 %",
    ingen: "Ingen",
  },
  {
    label: "UV-reduktion (skydd mot blekning)",
    icon: Shield,
    prestige: "99 %",
    tonad: "≥ 99 %",
    ingen: "Mycket låg",
  },
  {
    label: "Reflektion utifrån (spegelblankt utseende)",
    icon: Eye,
    prestige: "Låg — neutral fasad",
    tonad: "Hög — spegelblank",
    ingen: "Ingen",
  },
  {
    label: "Metallfri (påverkar inte mobil-/wifi-signal)",
    icon: Check,
    prestige: "Ja",
    tonad: "Nej (oftast metalliserad)",
    ingen: "—",
  },
  {
    label: "Korrosionsfri över tid",
    icon: Check,
    prestige: "Ja",
    tonad: "Risk för missfärgning",
    ingen: "—",
  },
  {
    label: "Tillverkargaranti",
    icon: Shield,
    prestige: "Upp till 15 år",
    tonad: "Vanligen 7–10 år",
    ingen: "—",
  },
];

type Datasheet = {
  title: string;
  desc: string;
  href: string;
};

const datasheets: Datasheet[] = [
  {
    title: "3M Prestige 70 — produktblad (PDF)",
    desc: "Klar solfilm med 68 % VLT, 50 % TSER och 23 % bländningsreduktion. Den mest sökta varianten i Sverige.",
    href: "/datablad/3m-prestige-70.pdf",
  },
  {
    title: "Se alla datablad",
    desc: "Datablad för Prestige Exterior och övriga fönsterfilmer.",
    href: "/datablad",
  },
];

function PrestigePage() {
  return (
    <>
      <PageHero
        eyebrow="3M PRESTIGE"
        title="Prestige-solfilm — hög prestanda utan toning, klar sikt och starkt skydd"
        subtitle="3M Prestige har över 200 mikroskopiska polymerlager som reflekterar värme och UV. Solfilmen är metallfri och släpper in naturligt ljus utan att göra fasaden spegelblank."
        image={BBN_PHOTOS.prestigeHero}
        breadcrumbs={[{ label: "3M Prestige" }]}
      />
      <PostHeroConversion />




      {/* Jämförelsetabell */}
      <section className="py-20 md:py-28 bg-navy-light border-y border-border">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">JÄMFÖRELSE</p>
            <h2 className="mt-3 text-3xl md:text-4xl max-w-3xl">
              Jämför Prestige med vanlig tonad solfilm och obehandlat glas.
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left font-medium text-muted-foreground py-4 pr-4 w-1/3">
                      Egenskap
                    </th>
                    <th className="text-left py-4 px-4 bg-gold/10 border-l border-r border-gold/30">
                      <span className="block text-[0.65rem] uppercase tracking-[0.2em] text-gold">
                        Rekommenderad
                      </span>
                      <span className="block mt-1 text-base font-semibold text-foreground">
                        3M Prestige
                      </span>
                    </th>
                    <th className="text-left py-4 px-4">
                      <span className="block text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
                        Alternativ
                      </span>
                      <span className="block mt-1 text-base font-semibold text-foreground">
                        Standard tonad solfilm
                      </span>
                    </th>
                    <th className="text-left py-4 px-4">
                      <span className="block text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
                        Referens
                      </span>
                      <span className="block mt-1 text-base font-semibold text-foreground">
                        Ingen film
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row) => {
                    const Icon = row.icon;
                    return (
                      <tr
                        key={row.label}
                        className="border-b border-border/60 hover:bg-background/40 transition-colors"
                      >
                        <td className="py-4 pr-4 align-top">
                          <div className="flex items-start gap-3">
                            <Icon size={16} className="text-gold mt-0.5 shrink-0" />
                            <span className="text-foreground font-medium">{row.label}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 align-top bg-gold/[0.06] border-l border-r border-gold/20 text-foreground font-medium">
                          {row.prestige}
                        </td>
                        <td className="py-4 px-4 align-top text-muted-foreground">{row.tonad}</td>
                        <td className="py-4 px-4 align-top text-muted-foreground">
                          {row.ingen === "—" ? (
                            <X size={16} className="text-muted-foreground/60" />
                          ) : (
                            row.ingen
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-6 flex items-start gap-2 text-xs text-muted-foreground font-light max-w-3xl">
              <Info size={14} className="mt-0.5 shrink-0 text-gold" />
              Värdena gäller Prestige 70. Exakta
              specifikationer anges i 3M:s officiella datablad. Kontakta oss
              för rekommendation utifrån just dina fönster och väderstreck.
            </p>
          </Reveal>
        </div>
      </section>


      {/* Intro / produktbeskrivning */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <Reveal>
            <p className="section-number">PRODUKTBESKRIVNING</p>
            <h2 className="mt-3 text-3xl md:text-4xl leading-tight">
              Nanoteknik istället för metall.
            </h2>
            <div className="mt-6 space-y-5 text-muted-foreground font-light leading-relaxed">
              <p>
                Bakom 3M Prestige ligger decennier av materialforskning. Istället för en tunn
                metallbeläggning används över 200 mikroskopiska polymerlager som tillsammans
                reflekterar bort den infraröda värmestrålningen och släpper igenom det
                synliga ljuset som vi vill ha in i rummet.
              </p>
              <p>
                Du får in mindre värme och slipper en del av bländningen utan att fönstret ser
                tonat eller spegelblankt ut. Eftersom filmen är helt metallfri stör den inte
                mobiltäckning, wifi eller GPS. Den korroderar inte heller över tid.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Sun, h: "Upp till ≈ 97 % IR-reduktion", p: "Reflekterar värmestrålningen som värmer upp rummet." },
                { icon: Eye, h: "Klar sikt, naturligt ljus", p: "Fönstret behåller sitt neutrala utseende utan kraftig toning." },
                { icon: Shield, h: "99 % UV-blockerande", p: "Skyddar inredning, textilier och hud mot blekning." },
                { icon: Check, h: "Metallfri & korrosionsfri", p: "Påverkar inte signaler. Försämras inte över tid." },
              ].map((b) => (
                <div key={b.h} className="glass p-6">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded bg-gold/15 text-gold">
                    <b.icon size={18} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold">{b.h}</h3>
                  <p className="mt-2 text-sm text-muted-foreground font-light">{b.p}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* === Invändig Prestige 70 — verifierat mot 3M produktblad (Clear 1/4") === */}
      <section id="varianter" className="py-20 md:py-28 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">PRESTIGE-VARIANTER</p>
            <h2 className="mt-3 text-3xl md:text-4xl max-w-3xl">
              Prestige 70 — klar solfilm för invändig montering.
            </h2>
            <p className="mt-4 text-muted-foreground font-light max-w-2xl">
              För invändig montering använder vi Prestige 70. Värdena nedan är hämtade från 3M:s
              officiella produktblad på 1/4" Clear glas.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 overflow-x-auto rounded-2xl border border-gold/15">
              <table className="w-full min-w-[760px] border-collapse text-sm">
                <caption className="sr-only">3M Prestige-varianter — VLT, TSER, UV och bländningsreduktion enligt 3M:s datablad.</caption>
                <thead>
                  <tr className="bg-navy-light/40 border-b border-gold/15">
                    <th scope="col" className="text-left py-4 px-4 font-semibold text-foreground">Variant</th>
                    <th scope="col" className="text-left py-4 px-4 font-semibold text-muted-foreground">Synligt ljus (VLT)</th>
                    <th scope="col" className="text-left py-4 px-4 font-semibold text-muted-foreground">Solenergi avvisad (TSER)</th>
                    <th scope="col" className="text-left py-4 px-4 font-semibold text-muted-foreground">UV-blockering</th>
                    <th scope="col" className="text-left py-4 px-4 font-semibold text-muted-foreground">Bländningsreduktion</th>
                    <th scope="col" className="text-left py-4 px-4 font-semibold text-muted-foreground">Bäst för</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { v: "Prestige 70", vlt: "68 %", tser: "50 %", uv: "99 %", glare: "23 %", best: "Bostäder & kontor där full utsikt och dagsljus prioriteras. Mest sålda varianten i Sverige." , highlight: true },
                  ].map((r, i) => (
                    <tr key={r.v} className={i % 2 ? "bg-navy-light/10" : ""}>
                      <th scope="row" className={`text-left py-4 px-4 font-semibold ${r.highlight ? "text-gold-bright" : "text-foreground"}`}>
                        {r.highlight && <span className="block text-[0.65rem] uppercase tracking-[0.2em] text-gold font-semibold mb-0.5">Mest sökt</span>}
                        {r.v}
                      </th>
                      <td className="py-4 px-4 text-foreground font-medium">{r.vlt}</td>
                      <td className="py-4 px-4 text-foreground font-medium">{r.tser}</td>
                      <td className="py-4 px-4 text-muted-foreground">{r.uv}</td>
                      <td className="py-4 px-4 text-muted-foreground">{r.glare}</td>
                      <td className="py-4 px-4 text-muted-foreground">{r.best}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground font-light max-w-3xl">
              <Info size={14} className="mt-0.5 shrink-0 text-gold" />
              Värden enligt 3M:s produktblad <em>3M™ Sun Control Window Film Prestige 70</em> (ref. 1400301O), reference 1/4" Clear glas. Värden på 2-glas (Double Clear) och tonat glas finns i
              respektive datablad. Vi tar med exakta värden för din glastyp i offerten.
            </p>
          </Reveal>

          {/* Pris per variant */}
          <Reveal delay={0.2}>
            <div className="mt-10 rounded-2xl border border-gold/20 bg-card/50 p-6 md:p-8">
              <h3 className="font-display text-xl md:text-2xl text-foreground">3M Prestige 70 — vad kostar det?</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                Här ser du priset för Skåne 2026, inklusive material, installation och 10 års produktgaranti:
              </p>
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { v: "Prestige 70", price: "1 100–1 600 kr/m²" },
                ].map((p) => (
                  <div key={p.v} className="rounded-xl border border-border bg-background/40 p-4">
                    <div className="text-sm font-medium text-foreground">{p.v}</div>
                    <div className="mt-1 font-display text-lg text-gold-bright">{p.price}</div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Prisspannen varierar med fönsteryta, antal rutor och arbetshöjd.
                Se vår <Link to="/priser" className="text-gold-bright underline-offset-2 hover:underline">prissida</Link> för
                kalkylator, eller <Link to="/offert" className="text-gold-bright underline-offset-2 hover:underline">begär fast pris</Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* === Invändig vs utvändig — Prestige Exterior === */}
      <section id="exterior" className="py-20 md:py-28 bg-navy-light border-y border-border scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">INVÄNDIG & UTVÄNDIG</p>
            <h2 className="mt-3 text-3xl md:text-4xl max-w-3xl">
              Prestige finns för både invändig och utvändig montering.
            </h2>
            <p className="mt-4 text-muted-foreground font-light max-w-2xl">
              I 95 % av installationerna monteras filmen invändigt, där den skyddas mot väder, är enklare att byta och håller längre. När glaskonstruktionen inte tillåter invändig film finns Prestige Exterior.
            </p>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Reveal>
              <article className="h-full rounded-2xl border border-gold/30 bg-card/60 p-7">
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg bg-gold/15 p-2"><HomeIcon size={18} className="text-gold" /></div>
                  <h3 className="font-display text-xl text-foreground">Invändig Prestige (standard)</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Monteras på glasets insida och är skyddad från regn, smuts, vind och mekanisk åverkan. Bär 3M:s standardgaranti och har den längsta livslängden i serien.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-foreground/85">
                  <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Prestige 70 för invändig montering</li>
                  <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Längst livslängd och fullständig 3M-garanti</li>
                  <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Snabbast installation. På enklare våningar behövs inga ställningar</li>
                </ul>
              </article>
            </Reveal>

            <Reveal delay={0.1}>
              <article className="h-full rounded-2xl border border-gold/20 bg-card/40 p-7">
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg bg-gold/15 p-2"><Cloud size={18} className="text-gold" /></div>
                  <h3 className="font-display text-xl text-foreground">Prestige Exterior (utvändig)</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Har väderbeständig acrylatlimning och används när invändig film inte kan monteras, till exempel på vissa LowE-belagda glas, ytfastighetsbeläggningar eller där värmedifferensen riskerar thermal shock.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-foreground/85">
                  <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Prestige 40 Exterior, 50 Exterior, 70 Exterior och 90 Exterior</li>
                  <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Lösning för LowE-glas och tjocka belagda fasadrutor</li>
                  <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> 7–10 års garanti, jämfört med upp till 15 år för invändig film</li>
                  <li className="flex items-start gap-2"><Info size={16} className="text-muted-foreground mt-0.5 shrink-0" /> Kräver oftast ställning eller skylift</li>
                </ul>
              </article>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-8 rounded-2xl border border-gold/20 bg-gold/5 p-5 md:p-6 flex items-start gap-4">
              <Info size={20} className="text-gold-bright shrink-0 mt-0.5" />
              <p className="text-sm md:text-base text-foreground/85 leading-relaxed">
                <strong>"3M Prestige 70 Exterior" eller "70x"</strong> — båda termerna används om den utvändiga
                varianten av PR 70. Vi monterar både invändig och utvändig Prestige. Vid platsbesöket
                bedömer vi vilken som är rätt för just dina fönster utifrån glastyp, fasadhöjd och garantibehov.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* === Auktoriserad återförsäljare === */}
      <section id="aterforsaljare" className="py-16 md:py-20 scroll-mt-24">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-card/80 to-card/40 p-7 md:p-10">
              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-gold/15 p-3 shrink-0">
                  <Award size={24} className="text-gold" />
                </div>
                <div>
                  <p className="section-number">AUKTORISERAD 3M-ÅTERFÖRSÄLJARE</p>
                  <h2 className="mt-3 font-display text-2xl md:text-3xl text-foreground">
                    Vi är auktoriserad återförsäljare och installatör av 3M Prestige i Sverige.
                  </h2>
                  <p className="mt-4 text-muted-foreground leading-relaxed max-w-3xl">
                    Vi är <strong className="text-foreground">auktoriserad 3M-installatör</strong>, vilket
                    innebär att vi köper Prestige-film direkt från 3M med spårbara batch­nummer,
                    har certifierade montörer och kan utfärda 3M:s fulla 10-åriga produktgaranti.
                    Film köpt och monterad av icke-auktoriserade aktörer omfattas inte av denna garanti.
                  </p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-foreground/85">
                    <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Direktleverans från 3M — inga gråmarknadsfilmer</li>
                    <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Certifierad montörutbildning från 3M</li>
                    <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> 3M:s 10 års produktgaranti utfärdas vid installation</li>
                    <li className="flex items-start gap-2"><Check size={16} className="text-gold mt-0.5 shrink-0" /> +10 års garanti på vårt installationsarbete</li>
                  </ul>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link
                      to="/auktoriserad-installator"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-gold-bright hover:gap-3 transition-all"
                    >
                      Läs mer om vad auktoriserad installation innebär <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Tekniska datablad */}
      <section id="datablad" className="py-20 md:py-28">

        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">TEKNISKA DATABLAD</p>
            <h2 className="mt-3 text-3xl md:text-4xl max-w-3xl">
              Datablad för 3M Prestige och andra fönsterfilmer.
            </h2>
            <p className="mt-4 text-muted-foreground font-light max-w-2xl">
              Officiella specifikationer för 3M Prestige, 3M Ultra Säkerhetsfilm och Solar
              Screen-serien. Saknar du ett datablad? Hör av dig så ordnar vi det.
            </p>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {datasheets.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.05}>
                <a
                  href={d.href}
                  className="glass p-6 h-full flex flex-col group"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded bg-gold/15 text-gold">
                      <FileText size={20} />
                    </span>
                    <ExternalLink size={16} className="text-muted-foreground" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold">{d.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground font-light flex-1">
                    {d.desc}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold group-hover:gap-3 transition-all">
                    {d.href.endsWith(".pdf") ? "Öppna PDF" : "Se alla datablad"} <ArrowRight size={14} />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-8 text-center">
              <Link
                to="/datablad"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gold hover:gap-3 transition-all"
              >
                Se alla datablad <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Partners / varumärken */}
      <section className="py-16 md:py-20 bg-navy-light border-y border-border">
        <div className="mx-auto max-w-5xl px-5 md:px-10 text-center">
          <Reveal>
            <p className="section-number">PÅLITLIG PARTNER</p>
            <h2 className="mt-3 text-2xl md:text-3xl">
              Vi är auktoriserad installatör för 3M och Solar Screen.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-12 md:gap-20">
              <div className="flex flex-col items-center">
                <img
                  src={logo3M.url}
                  alt="3M logotyp"
                  width={120}
                  height={48}
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-auto object-contain"
                />
                <div className="mt-3 h-0.5 w-12 bg-gold" />
              </div>
              <div className="flex flex-col items-center">
                <img
                  src={solarScreenLogo.url}
                  alt="Solar Screen logotyp"
                  width={160}
                  height={40}
                  loading="lazy"
                  decoding="async"
                  className="h-8 w-auto object-contain"
                />
                <div className="mt-3 h-0.5 w-12 bg-gold" />
              </div>
              <div className="flex flex-col items-center">
                <span className="font-display text-2xl md:text-3xl font-bold tracking-[0.18em] text-foreground">
                  NoTrax
                </span>
                <div className="mt-3 h-0.5 w-12 bg-gold" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-5 md:px-10 text-center">
          <Reveal>
            <p className="section-number">RÅDGIVNING</p>
            <h2 className="mt-3 text-3xl md:text-5xl">
              Be om <span className="text-gold">rådgivning kring vilken Prestige-variant</span> som passar dina fönster.
            </h2>
            <p className="mt-5 text-muted-foreground font-light max-w-xl mx-auto">
              Vi tittar på fönstertyp, glaskonstruktion, väderstreck och hur du använder rummet.
              Sedan går vi igenom Prestige 70 och Prestige Exterior med dig. Rådgivningen är kostnadsfri.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-4 rounded font-semibold min-h-12"
              >
                Be om rådgivning <ArrowRight size={18} />
              </Link>
              <Link
                to="/offert"
                className="inline-flex items-center justify-center gap-2 border border-border text-foreground px-7 py-4 rounded font-semibold hover:border-gold hover:text-gold transition-colors min-h-12"
              >
                Begär offert
              </Link>
            </div>
          </Reveal>
        </div>
      </section>




      <CallbackRail product="Solfilm" />
    </>
  );
}
